import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Switch, IconButton, Tooltip, Button, TextField,
  Grid, Pagination, Stack, CircularProgress, ToggleButtonGroup, ToggleButton,
  Select, MenuItem, FormControl, InputLabel, FormControlLabel, Paper,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Refresh as RefreshIcon, Store as StoreIcon,
  Map as MapIcon, Place as PlaceIcon, Warehouse as WarehouseIcon,
  MyLocation as MyLocationIcon, ViewList as ViewListIcon, Room as RoomIcon,
} from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import api from '../api/axios';
import { PageHeader, Toolbar, DataTable, StatusBadge, FormDialog, useFeedback } from '../components/ui';
import { color, radius } from '../theme/tokens';

// Leaflet's default marker images break under a bundler unless re-pointed.
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const INDIA_CENTER = [22.9734, 78.6569];

const EMPTY = {
  storecode: '', storename: '', address1: '', city: '', state: '', pin: '', phone: '',
  cell: '', email: '', store_type: 'Retail', manager: '', region: '', gstno: '', act_status: 1,
  latitude: '', longitude: '', fulfilment_enabled: 0, is_warehouse: 0,
};

const FIELDS = [
  { key: 'storecode',  label: 'Store Code',    required: true, sm: 6 },
  { key: 'storename',  label: 'Store Name',    required: true, sm: 6 },
  { key: 'address1',   label: 'Address',                       sm: 12 },
  { key: 'city',       label: 'City',                          sm: 6 },
  { key: 'state',      label: 'State',                         sm: 6 },
  { key: 'pin',        label: 'PIN',                           sm: 6 },
  { key: 'phone',      label: 'Phone',                         sm: 6 },
  { key: 'cell',       label: 'Mobile',                        sm: 6 },
  { key: 'email',      label: 'Email',                         sm: 6 },
  { key: 'manager',    label: 'Store Manager',                 sm: 6 },
  { key: 'region',     label: 'Region',                        sm: 6 },
  { key: 'gstno',      label: 'GST Number',                    sm: 6 },
  { key: 'store_type', label: 'Store Type',                    sm: 6 },
];

const gmapsLink = (lat, lng) => `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;

// ── Leaflet helpers ───────────────────────────────────────────────

const ClickToPlace = ({ onPick }) => {
  useMapEvents({ click: (e) => onPick(e.latlng.lat, e.latlng.lng) });
  return null;
};

// Keeps the viewport in sync when the pin is changed from the text fields.
const Recenter = ({ lat, lng }) => {
  const map = useMap();
  useEffect(() => {
    if (lat != null && lng != null) map.setView([lat, lng], Math.max(map.getZoom(), 14));
  }, [lat, lng, map]);
  return null;
};

const LocationPicker = ({ latitude, longitude, onChange }) => {
  const [searchQuery, setSearchQuery] = React.useState('');
  const [searchResults, setSearchResults] = React.useState([]);
  const [searching, setSearching] = React.useState(false);
  const [showResults, setShowResults] = React.useState(false);
  const searchTimeoutRef = React.useRef(null);

  const lat = latitude === '' || latitude == null ? null : parseFloat(latitude);
  const lng = longitude === '' || longitude == null ? null : parseFloat(longitude);
  const valid = lat !== null && lng !== null && !Number.isNaN(lat) && !Number.isNaN(lng);

  // Debounced search
  const handleSearch = React.useCallback(async (query) => {
    if (!query || query.trim().length < 3) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&countrycodes=in&limit=5`
      );
      const data = await response.json();
      setSearchResults(data || []);
      setShowResults(true);
    } catch (err) {
      console.error('Search failed:', err);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  React.useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    if (searchQuery.trim().length >= 3) {
      searchTimeoutRef.current = setTimeout(() => {
        handleSearch(searchQuery);
      }, 500);
    } else {
      setSearchResults([]);
      setShowResults(false);
    }
    
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, handleSearch]);

  const handleResultClick = (result) => {
    const newLat = parseFloat(result.lat).toFixed(6);
    const newLng = parseFloat(result.lon).toFixed(6);
    onChange(newLat, newLng);
    setSearchQuery('');
    setShowResults(false);
    setSearchResults([]);
  };

  return (
    <Box>
      {/* Search box */}
      <Box sx={{ position: 'relative', mb: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search location (e.g., Hotspot Store Nehru Place)"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={() => searchResults.length > 0 && setShowResults(true)}
          InputProps={{
            startAdornment: <MapIcon sx={{ fontSize: 18, mr: 1, color: color.ink[400] }} />,
            endAdornment: searching && <CircularProgress size={18} />,
          }}
          sx={{ 
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#f8fafc',
            }
          }}
        />
        
        {/* Search results dropdown */}
        {showResults && searchResults.length > 0 && (
          <Paper
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              zIndex: 1300,
              maxHeight: 280,
              overflow: 'auto',
              mt: 0.5,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            }}
          >
            {searchResults.map((result, idx) => (
              <Box
                key={idx}
                onClick={() => handleResultClick(result)}
                sx={{
                  p: 1.5,
                  cursor: 'pointer',
                  borderBottom: idx < searchResults.length - 1 ? `1px solid ${color.border}` : 'none',
                  '&:hover': { backgroundColor: '#f8fafc' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <PlaceIcon sx={{ fontSize: 18, color: color.primary[600], flexShrink: 0, mt: 0.25 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                      {result.display_name.split(',')[0]}
                    </Typography>
                    <Typography variant="caption" sx={{ color: color.ink[600], display: 'block', mt: 0.25 }}>
                      {result.display_name.split(',').slice(1).join(',').trim()}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ))}
          </Paper>
        )}
      </Box>

      <Box
        sx={{
          height: 260,
          borderRadius: `${radius.md}px`,
          overflow: 'hidden',
          border: `1px solid ${color.border}`,
          '& .leaflet-container': { fontFamily: 'inherit' },
        }}
      >
        <MapContainer
          center={valid ? [lat, lng] : INDIA_CENTER}
          zoom={valid ? 14 : 4}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom
        >
          <TileLayer
            attribution='&copy; OpenStreetMap contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <ClickToPlace onPick={(la, ln) => onChange(la.toFixed(6), ln.toFixed(6))} />
          <Recenter lat={lat} lng={lng} />
          {valid && <Marker position={[lat, lng]} />}
        </MapContainer>
      </Box>
      <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: color.ink[500] }}>
        Search for a location above, or click the map to drop the store pin, or type coordinates in the fields.
      </Typography>
      {valid && (
        <Button
          size="small"
          href={gmapsLink(lat, lng)}
          target="_blank"
          rel="noopener noreferrer"
          startIcon={<MapIcon sx={{ fontSize: 16 }} />}
          sx={{ mt: 0.5, px: 0.5 }}
        >
          Verify on Google Maps
        </Button>
      )}
    </Box>
  );
};

// ── Edit / add dialog ─────────────────────────────────────────────

const StoreDialog = ({ open, onClose, store, onSaved }) => {
  const feedback = useFeedback();
  const editing = !!store;
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [locating, setLocating] = useState(false);
  const [error, setError]   = useState('');

  useEffect(() => {
    if (open) {
      setForm(store ? {
        storecode: store.STORECODE || '', storename: store.STORENAME || '',
        address1: store.ADDRESS1 || '', city: store.CITY || '', state: store.STATE || '',
        pin: store.PIN || '', phone: store.PHONE || '', cell: store.CELL || '',
        email: store.EMAIL || '', store_type: store.STORE_TYPE || 'Retail',
        manager: store.StoreManager || '', region: store.REGION || '',
        gstno: store.GSTNO || '', act_status: store.ACTIVE_STATUS ?? 1,
        latitude:  store.LATITUDE  != null ? String(store.LATITUDE)  : '',
        longitude: store.LONGITUDE != null ? String(store.LONGITUDE) : '',
        fulfilment_enabled: store.FULFILMENT_ENABLED ?? 0,
        is_warehouse: store.IS_WAREHOUSE ?? 0,
      } : EMPTY);
      setError('');
    }
  }, [open, store]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.storecode || !form.storename) { setError('Store code and name are required'); return; }
    if ((form.latitude === '') !== (form.longitude === '')) {
      setError('Provide both latitude and longitude, or leave both empty'); return;
    }
    setSaving(true); setError('');
    try {
      let srno = store?.SRNO;
      if (editing) await api.put(`/api/admin/stores/${srno}`, form);
      else {
        const created = await api.post('/api/admin/stores', form);
        srno = created.data.srno;
      }

      await api.put(`/api/admin/stores/${srno}/geo`, {
        latitude:  form.latitude,
        longitude: form.longitude,
        fulfilment_enabled: form.fulfilment_enabled,
        is_warehouse: form.is_warehouse,
      });

      feedback.success(editing ? 'Store updated' : 'Store added');
      onSaved(); onClose();
    } catch (err) { setError(err.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const warehouse = form.is_warehouse === 1;

  // Look the pin up from the store's address (OpenStreetMap). Lookup only —
  // the admin still reviews it on the map and saves.
  const handleAutoLocate = async () => {
    if (!store?.SRNO) { setError('Save the store first, then auto-locate it from its address'); return; }
    setLocating(true); setError('');
    try {
      const res = await api.post(`/api/admin/stores/${store.SRNO}/geocode`);
      const { latitude, longitude, matched, precision } = res.data.data;
      setForm(f => ({ ...f, latitude: String(latitude), longitude: String(longitude) }));
      const where = (matched || '').split(',').slice(0, 3).join(',');
      // PIN-centre matches are the least precise, so say so — the admin is expected to nudge the pin.
      const how = precision === 'postal' ? 'PIN area' : precision === 'city' ? 'city centre' : 'matched address';
      feedback.success(`Placed near ${where} (${how}) — check the pin`);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not locate this address — please pin it manually');
    } finally { setLocating(false); }
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={editing ? 'Edit Store' : 'Add New Store'}
      subtitle={editing ? 'Update the store details and its map location.' : 'Fill in the details to create a new store.'}
      icon={<StoreIcon sx={{ fontSize: 18 }} />}
      maxWidth="md"
      saving={saving}
      error={error}
      actions={
        <>
          <Button onClick={onClose} disabled={saving} variant="outlined" color="inherit">Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={18} color="inherit" /> : (editing ? 'Save Changes' : 'Add Store')}
          </Button>
        </>
      }
    >
      <Grid container spacing={2} sx={{ pt: 0.5 }}>
        {FIELDS.map(f => (
          <Grid key={f.key} size={{ xs: 12, sm: f.sm }}>
            <TextField
              fullWidth
              size="small"
              label={f.required ? `${f.label} *` : f.label}
              value={form[f.key] || ''}
              onChange={e => set(f.key, e.target.value)}
            />
          </Grid>
        ))}

        {/* ── Location ─────────────────────────────────────────── */}
        <Grid size={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, mb: 0.5 }}>
            <PlaceIcon sx={{ fontSize: 18, color: color.primary[600] }} />
            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: color.ink[700] }}>
              Map Location
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Tooltip title={editing ? 'Look the pin up from the store address' : 'Save the store first, then auto-locate'}>
              <span>
                <Button
                  size="small"
                  onClick={handleAutoLocate}
                  disabled={locating || !editing}
                  startIcon={locating
                    ? <CircularProgress size={14} color="inherit" />
                    : <MyLocationIcon sx={{ fontSize: 16 }} />}
                >
                  {locating ? 'Locating…' : 'Auto-locate'}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth size="small" label="Latitude"
            placeholder="e.g. 28.6139"
            value={form.latitude}
            onChange={e => set('latitude', e.target.value)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth size="small" label="Longitude"
            placeholder="e.g. 77.2090"
            value={form.longitude}
            onChange={e => set('longitude', e.target.value)}
          />
        </Grid>
        <Grid size={12}>
          <LocationPicker
            latitude={form.latitude}
            longitude={form.longitude}
            onChange={(la, ln) => setForm(f => ({ ...f, latitude: la, longitude: ln }))}
          />
        </Grid>

        {/* ── Flags ────────────────────────────────────────────── */}
        <Grid size={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap', mt: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.act_status === 1}
                  onChange={e => set('act_status', e.target.checked ? 1 : 0)}
                />
              }
              label={<Typography variant="body2">Active</Typography>}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.is_warehouse === 1}
                  onChange={e => setForm(f => ({
                    ...f,
                    is_warehouse: e.target.checked ? 1 : 0,
                    // a warehouse can never fulfil online orders
                    fulfilment_enabled: e.target.checked ? 0 : f.fulfilment_enabled,
                  }))}
                />
              }
              label={<Typography variant="body2">Warehouse / DC</Typography>}
            />
            <Tooltip title={warehouse ? 'Warehouses cannot fulfil online orders' : ''}>
              <span>
                <FormControlLabel
                  disabled={warehouse}
                  control={
                    <Switch
                      checked={form.fulfilment_enabled === 1}
                      onChange={e => set('fulfilment_enabled', e.target.checked ? 1 : 0)}
                    />
                  }
                  label={<Typography variant="body2">Can fulfil online orders</Typography>}
                />
              </span>
            </Tooltip>
          </Box>
        </Grid>
      </Grid>
    </FormDialog>
  );
};

// ── Map View ──────────────────────────────────────────────────────

// Search control component
const MapSearch = ({ onLocationFound }) => {
  const [query, setQuery] = React.useState('');
  const [searching, setSearching] = React.useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    
    setSearching(true);
    try {
      // Use Nominatim (OpenStreetMap) geocoding
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&countrycodes=in&limit=1`
      );
      const data = await response.json();
      
      if (data && data.length > 0) {
        const { lat, lon, display_name } = data[0];
        onLocationFound(parseFloat(lat), parseFloat(lon), display_name);
      } else {
        alert('Location not found. Try a different search term.');
      }
    } catch (err) {
      alert('Search failed. Please try again.');
    } finally {
      setSearching(false);
    }
  };

  return (
    <Paper
      component="form"
      onSubmit={handleSearch}
      sx={{
        position: 'absolute',
        top: 10,
        left: 60,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        width: 320,
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      }}
    >
      <TextField
        size="small"
        placeholder="Search location on map..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        disabled={searching}
        sx={{ flex: 1, '& fieldset': { border: 'none' } }}
      />
      <IconButton type="submit" disabled={searching} size="small">
        {searching ? <CircularProgress size={20} /> : <MapIcon />}
      </IconButton>
    </Paper>
  );
};

// Component to fly to location
const FlyToLocation = ({ lat, lng, zoom = 14 }) => {
  const map = useMap();
  
  React.useEffect(() => {
    if (lat != null && lng != null) {
      map.flyTo([lat, lng], zoom, { duration: 1.5 });
    }
  }, [lat, lng, zoom, map]);
  
  return null;
};

const StoresMapView = ({ stores, onEdit, loading }) => {
  const [searchResult, setSearchResult] = React.useState(null);
  
  const validStores = stores.filter(s => 
    s.LATITUDE != null && s.LONGITUDE != null && 
    !Number.isNaN(parseFloat(s.LATITUDE)) && !Number.isNaN(parseFloat(s.LONGITUDE))
  );

  const handleLocationFound = (lat, lng, name) => {
    setSearchResult({ lat, lng, name });
  };

  // Custom icon for active stores
  const activeIcon = new L.Icon({
    iconUrl: markerIcon,
    iconRetinaUrl: markerIcon2x,
    shadowUrl: markerShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
  });

  // Gray icon for inactive stores
  const inactiveIcon = new L.Icon({
    iconUrl: 'data:image/svg+xml;base64,' + btoa(`
      <svg width="25" height="41" xmlns="http://www.w3.org/2000/svg">
        <path d="M12.5 0C5.6 0 0 5.6 0 12.5c0 8.4 12.5 28.5 12.5 28.5S25 20.9 25 12.5C25 5.6 19.4 0 12.5 0z" 
              fill="#9e9e9e" stroke="#757575" stroke-width="1"/>
        <circle cx="12.5" cy="12.5" r="6" fill="#fff"/>
      </svg>
    `),
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
  });

  // Search result icon (highlighted)
  const searchIcon = new L.Icon({
    iconUrl: 'data:image/svg+xml;base64,' + btoa(`
      <svg width="25" height="41" xmlns="http://www.w3.org/2000/svg">
        <path d="M12.5 0C5.6 0 0 5.6 0 12.5c0 8.4 12.5 28.5 12.5 28.5S25 20.9 25 12.5C25 5.6 19.4 0 12.5 0z" 
              fill="#f59e0b" stroke="#d97706" stroke-width="1.5"/>
        <circle cx="12.5" cy="12.5" r="6" fill="#fff"/>
      </svg>
    `),
    iconSize: [30, 49],
    iconAnchor: [15, 49],
    popupAnchor: [1, -41],
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '600px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (validStores.length === 0) {
    return (
      <Paper sx={{ p: 6, textAlign: 'center' }}>
        <PlaceIcon sx={{ fontSize: 64, color: color.ink[300], mb: 2 }} />
        <Typography variant="h6" sx={{ mb: 1 }}>No stores with locations</Typography>
        <Typography variant="body2" color="text.secondary">
          Add latitude/longitude to stores to see them on the map
        </Typography>
      </Paper>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        height: '600px',
        borderRadius: `${radius.lg}px`,
        overflow: 'hidden',
        border: `1px solid ${color.border}`,
        '& .leaflet-container': { fontFamily: 'inherit', height: '100%', width: '100%' },
      }}
    >
      <MapSearch onLocationFound={handleLocationFound} />
      
      <MapContainer
        center={INDIA_CENTER}
        zoom={5}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        {searchResult && (
          <>
            <FlyToLocation lat={searchResult.lat} lng={searchResult.lng} zoom={14} />
            <Marker position={[searchResult.lat, searchResult.lng]} icon={searchIcon}>
              <Popup>
                <Box sx={{ minWidth: 200 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Search Result
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.8125rem', mb: 1 }}>
                    {searchResult.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {searchResult.lat.toFixed(6)}, {searchResult.lng.toFixed(6)}
                  </Typography>
                </Box>
              </Popup>
            </Marker>
          </>
        )}
        
        <MarkerClusterGroup chunkedLoading>
          {validStores.map((store) => {
            const lat = parseFloat(store.LATITUDE);
            const lng = parseFloat(store.LONGITUDE);
            const active = store.ACTIVE_STATUS === 1;
            
            return (
              <Marker
                key={store.SRNO}
                position={[lat, lng]}
                icon={active ? activeIcon : inactiveIcon}
              >
                <Popup>
                  <Box sx={{ minWidth: 200 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {store.STORENAME}
                    </Typography>
                    <Typography variant="caption" display="block" sx={{ color: color.ink[600], mb: 1 }}>
                      Code: {store.STORECODE}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.8125rem', mb: 0.5 }}>
                      {store.CITY}, {store.STATE} - {store.PIN}
                    </Typography>
                    {store.PHONE && (
                      <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: color.ink[600] }}>
                        📞 {store.PHONE}
                      </Typography>
                    )}
                    <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {!active && <StatusBadge label="Inactive" tone="neutral" size="small" />}
                      {store.IS_WAREHOUSE === 1 && <StatusBadge label="Warehouse" tone="neutral" size="small" />}
                      {store.FULFILMENT_ENABLED === 1 && <StatusBadge label="Fulfilment" tone="success" size="small" />}
                    </Box>
                    <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => onEdit(store)}
                        startIcon={<EditIcon sx={{ fontSize: 14 }} />}
                        sx={{ fontSize: '0.75rem', py: 0.25 }}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        href={gmapsLink(lat, lng)}
                        target="_blank"
                        rel="noopener noreferrer"
                        startIcon={<MapIcon sx={{ fontSize: 14 }} />}
                        sx={{ fontSize: '0.75rem', py: 0.25 }}
                      >
                        Google Maps
                      </Button>
                    </Box>
                  </Box>
                </Popup>
              </Marker>
            );
          })}
        </MarkerClusterGroup>
      </MapContainer>
    </Box>
  );
};

// ── Store list ────────────────────────────────────────────────────

const Stores = () => {
  const feedback = useFeedback();
  const [stores, setStores]       = useState([]);
  const [regions, setRegions]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [inputVal, setInputVal]   = useState('');
  const [search, setSearch]       = useState('');
  const [region, setRegion]       = useState('');
  const [fulfilment, setFulfilment] = useState('-1');
  const [page, setPage]           = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal]         = useState(0);
  const [dialogStore, setDialogStore] = useState(undefined);
  const [view, setView]           = useState('list'); // 'list' or 'map'

  const fetchStores = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/admin/stores', {
        params: {
          search: search || undefined,
          region: region || undefined,
          fulfilment,
          page, pagesize: 20,
        },
      });
      setStores(res.data.data.stores);
      setTotalPages(res.data.data.pagination.totalPages);
      setTotal(res.data.data.pagination.total);
    } catch {
      feedback.error('Failed to load stores');
    } finally {
      setLoading(false);
    }
  }, [search, region, fulfilment, page, feedback]);

  const fetchRegions = async () => {
    try {
      const res = await api.get('/api/admin/store-regions');
      setRegions(res.data.data || []);
    } catch {}
  };

  useEffect(() => { fetchStores(); }, [fetchStores]);
  useEffect(() => { fetchRegions(); }, []);
  useEffect(() => { const t = setTimeout(() => { setSearch(inputVal); setPage(1); }, 400); return () => clearTimeout(t); }, [inputVal]);

  const handleToggle = async (srno) => {
    try {
      await api.patch(`/api/admin/stores/${srno}/toggle`);
      feedback.success('Store status updated');
      fetchStores();
    } catch {
      feedback.error('Failed to update store status');
    }
  };

  const handleFulfilment = async (srno) => {
    try {
      await api.patch(`/api/admin/stores/${srno}/fulfilment`);
      feedback.success('Fulfilment updated');
      fetchStores();
    } catch {
      feedback.error('Failed to update fulfilment');
    }
  };

  const columns = [
    {
      key: 'STORENAME', label: 'Store', width: 240,
      render: (v, row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {row.IS_WAREHOUSE
            ? <WarehouseIcon sx={{ fontSize: 18, color: color.ink[400] }} />
            : <StoreIcon sx={{ fontSize: 18, color: color.primary[600] }} />}
          <Typography variant="body2" sx={{ fontWeight: 500 }}>{row.STORENAME || '—'}</Typography>
        </Box>
      ),
    },
    {
      key: 'STORECODE', label: 'Code',
      render: (v) => (
        <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
          {v || '—'}
        </Typography>
      ),
    },
    {
      key: 'CITY', label: 'City / State',
      render: (v, row) => {
        const place = [row.CITY, row.STATE].filter(Boolean).join(', ');
        return place ? `${place}${row.PIN ? ` · ${row.PIN}` : ''}` : (row.PIN || '—');
      },
    },
    { key: 'PHONE', label: 'Phone', render: (v, row) => row.CELL || row.PHONE || '—' },
    { key: 'StoreManager', label: 'Manager' },
    {
      key: 'REGION', label: 'Region',
      render: (v) => <StatusBadge label={v || '—'} tone="neutral" dot={false} />,
    },
    {
      key: 'HAS_GEO', label: 'Location', width: 176,
      render: (v, row) => {
        const lat = row.LATITUDE, lng = row.LONGITUDE;
        if (lat == null || lng == null) {
          return (
            <Typography variant="caption" sx={{ color: color.ink[400] }}>
              Not set
            </Typography>
          );
        }
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: '0.72rem', color: color.ink[600] }}>
              {Number(lat).toFixed(4)}, {Number(lng).toFixed(4)}
            </Typography>
            <Tooltip title="Open in Google Maps">
              <IconButton
                size="small"
                component="a"
                href={gmapsLink(lat, lng)}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Open store location in Google Maps"
              >
                <MapIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      key: 'FULFILMENT_ENABLED', label: 'Fulfilment', align: 'center', width: 120,
      render: (v, row) => row.IS_WAREHOUSE
        ? <StatusBadge label="Warehouse" tone="neutral" dot={false} />
        : (
          <Tooltip title={row.FULFILMENT_ENABLED === 1 ? 'Can fulfil online orders' : 'Not fulfilling online orders'}>
            <span>
              <Switch
                size="small"
                checked={row.FULFILMENT_ENABLED === 1}
                disabled={row.ACTIVE_STATUS !== 1}
                onChange={() => handleFulfilment(row.SRNO)}
              />
            </span>
          </Tooltip>
        ),
    },
    {
      key: 'ACTIVE_STATUS', label: 'Active', align: 'center', width: 88,
      render: (v, row) => (
        <Switch size="small" checked={row.ACTIVE_STATUS === 1} onChange={() => handleToggle(row.SRNO)} />
      ),
    },
    {
      key: 'SRNO', label: 'Edit', align: 'right', width: 72,
      render: (v, row) => (
        <Tooltip title="Edit store">
          <IconButton size="small" onClick={() => setDialogStore(row)} aria-label="Edit store">
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        eyebrow="Network"
        title="Stores"
        subtitle={`${total} stores across India`}
        actions={
          <>
            <ToggleButtonGroup
              value={view}
              exclusive
              onChange={(_, v) => v && setView(v)}
              size="small"
              sx={{ mr: 1 }}
            >
              <ToggleButton value="list" aria-label="List view">
                <Tooltip title="List view">
                  <ViewListIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="map" aria-label="Map view">
                <Tooltip title="Map view">
                  <RoomIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
            <Tooltip title="Refresh">
              <span>
                <IconButton onClick={fetchStores} disabled={loading} aria-label="Refresh">
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogStore(null)}>
              Add store
            </Button>
          </>
        }
      />

      <Toolbar
        search={inputVal}
        onSearch={setInputVal}
        searchPlaceholder="Search by name, code or city..."
        onRefresh={fetchStores}
        refreshing={loading}
        sx={{ mb: 2.5 }}
      >
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>Region</InputLabel>
          <Select
            value={region}
            label="Region"
            onChange={e => { setRegion(e.target.value); setPage(1); }}
          >
            <MenuItem value="">All Regions</MenuItem>
            {regions.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 190 }}>
          <InputLabel>Fulfilment</InputLabel>
          <Select
            value={fulfilment}
            label="Fulfilment"
            onChange={e => { setFulfilment(e.target.value); setPage(1); }}
          >
            <MenuItem value="-1">All stores</MenuItem>
            <MenuItem value="1">Fulfils online orders</MenuItem>
            <MenuItem value="0">Does not fulfil</MenuItem>
          </Select>
        </FormControl>
      </Toolbar>

      {view === 'map' ? (
        <StoresMapView 
          stores={stores} 
          onEdit={setDialogStore} 
          loading={loading}
        />
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={stores}
            rowKey="SRNO"
            loading={loading}
            minWidth={1180}
            emptyTitle="No stores found"
            emptyDescription="Add your first store to start managing the network."
            emptyActionLabel="Add store"
            onEmptyAction={() => setDialogStore(null)}
            getRowSx={(row) => (row.ACTIVE_STATUS === 0 ? { opacity: 0.55 } : {})}
          />

          {totalPages > 1 && (
            <Stack alignItems="center" sx={{ mt: 3 }}>
              <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)} color="primary" />
            </Stack>
          )}
        </>
      )}

      <StoreDialog
        open={dialogStore !== undefined}
        onClose={() => setDialogStore(undefined)}
        store={dialogStore || null}
        onSaved={fetchStores}
      />
    </Box>
  );
};

export default Stores;
